import { Dispatch, SetStateAction, useContext, useEffect } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SATOSPortal } from "@/DataAdapter/SATOSPortal";

import { GroupList } from "@/model/Application/GroupList";
import { CreateMaterialHandlingProcedure } from "@/model/MaterialHandling/CreateMaterialHandlingProcedure";
import { DeleteMaterialHandling } from "@/model/MaterialHandling/DeleteMaterialHandling";
import { MaterialHandlingEquipment } from "@/model/MaterialHandling/MaterialHandlingEquipment";
import { MaterialHandlingFastening } from "@/model/MaterialHandling/MaterialHandlingFastening";
import { MaterialHandlingProcedure } from "@/model/MaterialHandling/MaterialHandlingProcedure";
import {
	MaterialHandlingSearchResponse,
	MaterialHandlingSearchResponseItem,
} from "@/model/MaterialHandling/MaterialHandlingSearchResponse";
import { MaterialHandlingUnit } from "@/model/MaterialHandling/MaterialHandlingUnit";
import { ReferenceUrl } from "@/model/MaterialHandling/ReferenceUrl";
import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";
import { SaveProcedureRequest } from "@/model/Procedure/ProcedureSaveRequest";
import { UserContext } from "@/model/User/UserContext";

import {
	ReadFromLocalStorage,
	WriteToLocalStorage,
	useLocalStorage,
} from "@/service/Utilities";


// TODO: Ustin - this whole file is a mess. A mix of queries, mutations, and local storage operations. Need to refactor it.
export const useFetchMaterialHandlingProcedure = (
	MaterialHandlingid: number | undefined,
	MaterialHandlingTagId: string,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingid,
			MaterialHandlingTagId,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingProcedure(
				MaterialHandlingid,
				MaterialHandlingTagId,
			),
		enabled: !!MaterialHandlingid,
	});
};

export const useFetchMaterialHandlingProcedureForEdit = (
	MaterialHandlingid: number | undefined,
	MaterialHandlingTagId: string,
	DoLoad: boolean,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingid,
			MaterialHandlingTagId,
			MaterialHandlingQueryKeys.edit,
		],
		queryFn: () =>
			SATOSPortal.GetMaterialHandlingProcedureForEdit(
				MaterialHandlingid,
				MaterialHandlingTagId,
			),
		enabled: !!MaterialHandlingid && DoLoad,
	});
};

export const SaveMaterialHandling = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MaterialHandlingProcedure) =>
			SATOSPortal.Updatematerialhandling(request),
		onMutate: (request: MaterialHandlingProcedure) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					MaterialHandlingQueryKeys.materialHandling,
					variables.MaterialHandlingId,
				],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveMaterialHandlingV2 = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MaterialHandlingProcedure) =>
			SATOSPortal.UpdatematerialhandlingV2(request),
		onMutate: (request: MaterialHandlingProcedure) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					MaterialHandlingQueryKeys.materialHandling,
					variables.MaterialHandlingId,
				],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const AddMaterialHandling = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: CreateMaterialHandlingProcedure) =>
			SATOSPortal.Addmaterialhandling(request),
		onMutate: (request: CreateMaterialHandlingProcedure) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: CreateMaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[
					MaterialHandlingQueryKeys.materialHandling,
					variables.MaterialHandlingId,
				],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						MaterialHandlingQueryKeys.materialHandling,
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const ImportMaterialHandlingDocument = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: number) =>
			SATOSPortal.ImportMaterialHandlingDockument(request),
		onMutate: (request: number) => {
			queryClient.cancelQueries({
				queryKey: [MaterialHandlingQueryKeys.import, request],
			});
			const previousValue: CreateMaterialHandlingProcedure | undefined =
				queryClient.getQueryData([MaterialHandlingQueryKeys.import, request]);

			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				[MaterialHandlingQueryKeys.import, variables],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.import, variables],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveUnit = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MaterialHandlingUnit) =>
			SATOSPortal.AddNewUnit(request),
		onMutate: (request: MaterialHandlingUnit) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			if (previousValue) {
				const updated = {
					...previousValue,
					UnitList: [...previousValue.UnitList, request],
				};
				queryClient.setQueryData<MaterialHandlingProcedure>(
					[
						MaterialHandlingQueryKeys.materialHandling,
						request.MaterialHandlingId,
					],
					updated,
				);
			}
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(["addnewunit", variables.UnitId], previousValue);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({ queryKey: ["addnewunit", variables] });
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveFastening = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MaterialHandlingFastening) =>
			SATOSPortal.AddNewFastening(request),
		onMutate: (request: MaterialHandlingFastening) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			if (previousValue) {
				const updated = {
					...previousValue,
					FasteningList: [...previousValue.FasteningList, request],
				};
				queryClient.setQueryData<MaterialHandlingProcedure>(
					[
						MaterialHandlingQueryKeys.materialHandling,
						request.MaterialHandlingId,
					],
					updated,
				);
			}
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				["addnewfastning", variables.FasteningId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						"addnewfastning",
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveEquipment = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: MaterialHandlingEquipment) =>
			SATOSPortal.AddNewEquipment(request),
		onMutate: (request: MaterialHandlingEquipment) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			if (previousValue) {
				const updated = {
					...previousValue,
					EquipmentList: [...previousValue.EquipmentList, request],
				};
				queryClient.setQueryData<MaterialHandlingProcedure>(
					[
						MaterialHandlingQueryKeys.materialHandling,
						request.MaterialHandlingId,
					],
					updated,
				);
			}
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				["addnewEquipment", variables.EquipmentId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: ["addnewEquipment", variables],
				});
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const SaveReference = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: ReferenceUrl) => SATOSPortal.AddNewReference(request),
		onMutate: (request: ReferenceUrl) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			if (previousValue) {
				const updated = {
					...previousValue,
					ReferenceList: [...previousValue.ReferenceList, request],
				};
				queryClient.setQueryData<MaterialHandlingProcedure>(
					[
						MaterialHandlingQueryKeys.materialHandling,
						request.MaterialHandlingId,
					],
					updated,
				);
			}
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				["addnewreference", variables.ReferenceId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: ["addnewreference", variables],
				});
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const DeleteMaterialhandingElement = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (request: DeleteMaterialHandling) =>
			SATOSPortal.DeleteMaterialhandingElement(request),

		onMutate: (request: DeleteMaterialHandling) => {
			queryClient.cancelQueries({
				queryKey: [
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				],
			});
			const previousValue: MaterialHandlingProcedure | undefined =
				queryClient.getQueryData([
					MaterialHandlingQueryKeys.materialHandling,
					request.MaterialHandlingId,
				]);

			if (previousValue) {
				const updated = {
					...previousValue,
				};
				queryClient.setQueryData<MaterialHandlingProcedure>(
					[
						MaterialHandlingQueryKeys.materialHandling,
						request.MaterialHandlingId,
					],
					updated,
				);
			}
			return previousValue;
		},
		// On failure, roll back to the previous value
		onError: (err, variables, previousValue) => {
			queryClient.setQueryData(
				["deletematerialitem", variables.KeyId],
				previousValue,
			);
		},
		// After success or failure, refetch the todos query
		onSettled: (variables) => {
			if (variables) {
				queryClient.invalidateQueries({
					queryKey: [
						"deletematerialitem",
						variables,
						{ refetchActive: true, refetchInactive: true },
					],
				});
				queryClient.invalidateQueries({
					queryKey: [MaterialHandlingQueryKeys.materialHandling],
				});
			}
		},
	});
	return mutation.mutateAsync;
};

export const useFetchUpdatedMaterialHandlingProcedures = (
	companyId: number | undefined,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.list,
			companyId,
		],
		queryFn: () => SATOSPortal.GetMaterialHandlingList(),
		enabled: !!companyId,
	});
};

export const useFetchMaterialHandlingProceduresWaitingForApproval = (
	companyId: number | undefined,
) => {
	return useQuery({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.list,
			MaterialHandlingQueryKeys.approve,
			companyId,
		],
		queryFn: () => SATOSPortal.GetMaterialHandlingListWaitingForApproval(),
		enabled: !!companyId,
	});
};

// TODO: This is used in procedure module. We shouldn't mix queries from different modules.
export const useFetchUserGroups = () => {
	const user = useContext(UserContext);
	return useQuery<GroupList, any>({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.groups,
			"GetUserGroups",
			user?.CompanyId,
		],
		queryFn: () => SATOSPortal.GetUserGroups(),
	});
};

export const useSearchMaterialHandlingTag = (searchTerm: string) => {
	return useQuery<MaterialHandlingSearchResponse>({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.search,
			searchTerm,
		],
		queryFn: () => SATOSPortal.SearchMaterialHandlingTag(searchTerm),
		enabled: !!searchTerm && !isEmpty(searchTerm),
	});
};

export const useFetchMaterialHandlingArciveListItem = (id: number) => {
	return useQuery({
		queryKey: [MaterialHandlingQueryKeys.materialHandling, id],
		queryFn: () => SATOSPortal.GetMaterialHandlingArciveListItem(id),
		enabled: !!id,
	});
};

export class MaterialHandlingQueryKeys {
	static readonly materialHandling = "materialHandling";
	static readonly approve = "approve";
	static readonly edit = "edit";
	static readonly list = "list";
	static readonly search = "search";
	static readonly groups = "groups";
	static readonly updated = "updated";
	static readonly import = "import";
}

export function isEmpty(text: string): boolean {
	return text == null || text.match(/^\s*$/) !== null;
}

export const useMaterialHandlingLocalFavorites = () => {
	return {
		favorites: ReadFromLocalStorage<Array<MaterialHandlingSearchResponseItem>>(
			"materialHandlingFavoriteState",
			[],
		),
		maxNumberOfFavorites: ReadFromLocalStorage<number>(
			"materialHandlingFavoriteMaxNumberOfFavorites",
			10,
		),
	};
};

export const useWriteMaterialHandlingLocalFavoritesToLocalStorage = (
	favorite: MaterialHandlingSearchResponseItem,
) => {
	const { favorites, maxNumberOfFavorites } =
		useMaterialHandlingLocalFavorites();

	const idx = favorites.findIndex(
		(fav) => fav.MaterialHandlingId === favorite?.MaterialHandlingId,
	);

	if (idx === -1) {
		WriteToLocalStorage<Array<MaterialHandlingSearchResponseItem>>(
			"materialHandlingFavoriteState",
			[favorite, ...getTrimmedFavorites(favorites, maxNumberOfFavorites)],
		);
	}
};

export const useFetchMaterialHandlingProcedureGroups = (companyId: number) => {
	return useQuery<ProcedureGroup[], any>({
		queryKey: [
			MaterialHandlingQueryKeys.materialHandling,
			MaterialHandlingQueryKeys.groups,
			companyId,
		],
		queryFn: () => SATOSPortal.QueryMaterialHandlingProcedureGroups(),
	});
};

// TODO: Ustin - Bad mutation, need onMutate, onError and onSuccess
export const SaveAndResetMaterialHandling = () => {
	const mutation = useMutation({
		mutationFn: (request: SaveProcedureRequest) =>
			SATOSPortal.SaveAndResetMaterialHandling(request),
	});
	return mutation.mutateAsync;
};

export const useFetchArchiveProcedureGroupFolders = (
	companyId: number,
): {
	groupList: ProcedureGroup[];
	setGroupList: Dispatch<SetStateAction<ProcedureGroup[]>>;
	closeAllProcedureGroupFolders: () => void;
	openAllProcedureGroupFolders: () => void;
} => {
	const [groupListLocalStorage, setGroupListLocalStorage] = useLocalStorage<
		ProcedureGroup[]
	>("materialHandlingFoldersState", []);

	const { data: groups } = useFetchMaterialHandlingProcedureGroups(companyId);

	useEffect(() => {
		if (!groupListLocalStorage || groupListLocalStorage.length === 0) {
			groups &&
				setGroupListLocalStorage(
					groups.sort((a, b) => sortArchiveGroupFolders(a, b)),
				);
		}

		if (groups && groupListLocalStorage) {
			if (groupListLocalStorage.length !== groups.length) {
				setGroupListLocalStorage(groups);
			} else {
				const sortedGroups = groups.sort((a, b) =>
					sortArchiveGroupFolders(a, b),
				);
				for (let i = 0; i < sortedGroups.length; i++) {
					if (
						sortedGroups[i].Id !== groupListLocalStorage[i].Id ||
						sortedGroups[i].Name !== groupListLocalStorage[i].Name
					) {
						setGroupListLocalStorage(sortedGroups);
						return;
					}
				}
			}
		}
	});

	const closeAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = false;
				return group;
			}),
		]);
	};

	const openAllProcedureGroupFolders = () => {
		setGroupListLocalStorage([
			...groupListLocalStorage.map((group) => {
				group.Expanded = true;
				return group;
			}),
		]);
	};

	return {
		groupList: groupListLocalStorage,
		setGroupList: setGroupListLocalStorage,
		closeAllProcedureGroupFolders,
		openAllProcedureGroupFolders,
	};
};

export const sortArchiveGroupFolders = (
	a: ProcedureGroup,
	b: ProcedureGroup,
): number => {
	if (a.SortNumber === 0 && b.SortNumber === 0) {
		return a.Name.localeCompare(b.Name);
	}

	if (a.SortNumber === 0) {
		return 1;
	}

	if (b.SortNumber === 0) {
		return -1;
	}

	return a.SortNumber - b.SortNumber;
};

const getTrimmedFavorites = (
	faves: Array<MaterialHandlingSearchResponseItem>,
	maxFavorites: number,
) => {
	if (faves.length >= maxFavorites) {
		return faves.slice(0, -1);
	}
	return faves;
};
